import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import TextArea from '../../../components/ui/TextArea.jsx';
import transactionService from '../../../services/transactionService.js';

const TransactionModal = ({ isOpen, onClose, onSuccess, transaction = null, selectedHotel }) => {
  const [formData, setFormData] = useState({
    type: 'EXPENSE',
    category: '',
    amount: '',
    description: '',
    reference: '',
    date: new Date().toISOString().split('T')[0],
    hotelId: selectedHotel?.id || ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isEditMode = !!transaction;

  // Categorias de despesas
  const expenseCategories = [
    { value: 'MAINTENANCE', label: 'Manutenção' },
    { value: 'CLEANING_SUPPLIES', label: 'Material de Limpeza' },
    { value: 'UTILITIES', label: 'Utilidades (Água, Luz, Internet)' },
    { value: 'STAFF_SALARY', label: 'Salários' },
    { value: 'MARKETING', label: 'Marketing' },
    { value: 'ADMINISTRATIVE', label: 'Administrativo' },
    { value: 'OTHER_EXPENSE', label: 'Outras Despesas' }
  ];

  // Categorias de receitas
  const incomeCategories = [
    { value: 'RESERVATION_PAYMENT', label: 'Pagamento de Reserva' },
    { value: 'SERVICE_FEE', label: 'Taxa de Serviço' },
    { value: 'OTHER_INCOME', label: 'Outras Receitas' }
  ];

  const typeOptions = [
    { value: 'EXPENSE', label: 'Despesa' },
    { value: 'INCOME', label: 'Receita' }
  ];

  useEffect(() => {
    if (transaction) {
      setFormData({
        type: transaction.type,
        category: transaction.category,
        amount: transaction.amount.toString(),
        description: transaction.description || '',
        reference: transaction.reference || '',
        date: transaction.date?.split('T')[0] || new Date().toISOString().split('T')[0],
        hotelId: transaction.hotelId
      });
    } else {
      setFormData({
        type: 'EXPENSE',
        category: '',
        amount: '',
        description: '',
        reference: '',
        date: new Date().toISOString().split('T')[0],
        hotelId: selectedHotel?.id || ''
      });
    }
  }, [transaction, selectedHotel, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validações
      if (!formData.hotelId) {
        setError('Por favor, selecione um hotel');
        setLoading(false);
        return;
      }

      if (!formData.category) {
        setError('Por favor, selecione uma categoria');
        setLoading(false);
        return;
      }

      if (!formData.amount || parseFloat(formData.amount) <= 0) {
        setError('Por favor, insira um valor válido');
        setLoading(false);
        return;
      }

      const transactionData = {
        ...formData,
        amount: parseFloat(formData.amount),
        date: new Date(formData.date).toISOString()
      };

      if (isEditMode) {
        await transactionService.update(transaction.id, transactionData);
      } else {
        await transactionService.create(transactionData);
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Erro ao salvar transação:', err);
      setError(err.response?.data?.message || 'Erro ao salvar transação');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Limpar categoria quando mudar o tipo
    if (field === 'type') {
      setFormData(prev => ({
        ...prev,
        category: ''
      }));
    }
  };

  if (!isOpen) return null;

  const categories = formData.type === 'EXPENSE' ? expenseCategories : incomeCategories;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-lg shadow-elevation-3 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon 
                name={formData.type === 'EXPENSE' ? 'TrendingDown' : 'TrendingUp'} 
                size={20} 
                color="var(--color-primary)" 
              />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                {isEditMode ? 'Editar Transação' : 'Nova Transação'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {isEditMode ? 'Atualize os dados da transação' : 'Registre uma nova despesa ou receita'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-smooth"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-start space-x-3">
              <Icon name="AlertCircle" size={20} color="var(--color-error)" />
              <div className="flex-1">
                <p className="text-sm text-error font-medium">Erro</p>
                <p className="text-sm text-error/80 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Tipo */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Tipo <span className="text-error">*</span>
            </label>
            <Select
              options={typeOptions}
              value={formData.type}
              onChange={(value) => handleChange('type', value)}
              required
            />
          </div>

          {/* Categoria */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Categoria <span className="text-error">*</span>
            </label>
            <Select
              options={categories}
              value={formData.category}
              onChange={(value) => handleChange('category', value)}
              placeholder="Selecione uma categoria"
              required
            />
          </div>

          {/* Valor e Data */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Valor (R$) <span className="text-error">*</span>
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => handleChange('amount', e.target.value)}
                placeholder="0,00"
                required
              />
            </div>

            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Data <span className="text-error">*</span>
              </label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => handleChange('date', e.target.value)}
                required
              />
            </div>
          </div>

          {/* Referência */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Referência
              <span className="text-muted-foreground font-normal ml-1">(opcional)</span>
            </label>
            <Input
              type="text"
              value={formData.reference}
              onChange={(e) => handleChange('reference', e.target.value)}
              placeholder="Ex: Nota fiscal #1234, Boleto, etc."
            />
          </div>

          {/* Descrição */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Descrição
              <span className="text-muted-foreground font-normal ml-1">(opcional)</span>
            </label>
            <TextArea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Adicione detalhes sobre esta transação..."
              rows={4}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName={isEditMode ? 'Save' : 'Plus'}
              iconPosition="left"
              disabled={loading}
            >
              {loading ? 'Salvando...' : (isEditMode ? 'Salvar Alterações' : 'Adicionar Transação')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TransactionModal;

